import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import SideNavLayout from "../customsidenav/sidenavlayout";
import { showToast } from "../reduxtoolkit/toastslice";
import { practiceShortName } from "../config/practice";
import { billingError, getConfig } from "./billingApi";
import ClaimsTab from "./ClaimsTab";
import GenerateTab from "./GenerateTab";
import FilesTab from "./FilesTab";
import ConfigTab from "./ConfigTab";
import { DummyPill } from "./ui";

/*
 * Billing module (/billing, owner request 10-09): build claims for any visit from our own data (or prefilled from
 * Athena's bill), keep them as drafts, then either create an 837P file (for the clearinghouse; Office Ally values
 * are DUMMY until the account exists, so files are ISA15=T) or submit through Athena (off unless the server
 * turns it on). The route and the nav item need the "Billing Module" Side Bar privilege; every server call
 * checks it again.
 *
 * Deep link from Note Review's Billing tab: /billing?date=YYYY-MM-DD&appointmentid=N opens that visit's claim.
 */

const DUMMY_KEYS = ["submitter_id", "receiver_id", "isa_sender_id", "isa_receiver_id", "gs_sender_code", "gs_receiver_code"];
export const isDummyConfig = (c) => DUMMY_KEYS.some((k) => /DUMMY/i.test(String(c?.clearinghouse?.[k] || "")));

const TABS = [["claims", "Claims"], ["generate", "Generate"], ["files", "Files"], ["config", "Config"]];

const BillingModule = () => {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const initialAppt = Number(params.get("appointmentid")) || null;
  const initialDate = /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") || "") ? params.get("date") : null;
  const [tab, setTab] = useState(TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "claims");
  const [config, setConfig] = useState(null);
  const [meta, setMeta] = useState(null);
  const [dummy, setDummy] = useState(false);
  const [athenaEnabled, setAthenaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filesKey, setFilesKey] = useState(0);

  const toast = useCallback((message, severity = "info") => dispatch(showToast({ message, severity })), [dispatch]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getConfig();
      setConfig(r.config);
      setMeta(r.meta);
      setDummy(Boolean(r.dummy));
      setAthenaEnabled(Boolean(r.athena_submit_enabled));
      setError(null);
    } catch (e) {
      setError(e?.response?.status === 403 ? "This sign-in doesn't have the Billing Module privilege."
                                           : billingError(e, "Could not load the billing config"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = `${practiceShortName()} - Billing`;
    loadConfig();
  }, [loadConfig]);

  return (
    <SideNavLayout>
      <div className="px-6 py-5 bg-[#FAFAF9] min-h-full">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl font-semibold text-[#18181B]">Billing</h1>
            <p className="text-[12px] text-[#6B7280]">Claims from our own visit data, 837P files for the clearinghouse, and billing setup.</p>
          </div>
          {dummy && (
            <div className="flex items-center gap-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              <DummyPill /> Clearinghouse ids are placeholders: files are for format testing only (ISA15=T).
            </div>
          )}
        </div>

        <div className="flex gap-1 border-b border-[#E5E7EB] mb-4" role="tablist">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === k ? "border-[#1D2951] text-[#1D2951]" : "border-transparent text-[#6B7280] hover:text-[#374151]"}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><CircularProgress size={28} /></div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 text-[13px] text-red-700">{error}</div>
        ) : (
          <>
            {tab === "claims" && <ClaimsTab config={config} initialDate={initialDate} initialAppointment={initialAppt} toast={toast} />}
            {tab === "generate" && <GenerateTab athenaEnabled={athenaEnabled} dummy={dummy} toast={toast}
              onFileCreated={() => setFilesKey((k) => k + 1)} />}
            {tab === "files" && <FilesTab toast={toast} refreshKey={filesKey} />}
            {tab === "config" && <ConfigTab config={config} meta={meta} toast={toast}
              onSaved={(c, m) => { setConfig(c); setMeta(m); setDummy(isDummyConfig(c)); }} />}
          </>
        )}
      </div>
    </SideNavLayout>
  );
};

export default BillingModule;
